import type { CatchGrant } from "@highmarch/app";
import type { LevelSim } from "@highmarch/domain";

/**
 * How the rest of the game reaches the pets (docs/PETS.md) without new constructor arguments: features/pets.ts sets
 * the link at boot (like skinsLink.ts). The cosmetics wear the equipped pet through it; the Skins picker lists the pets
 * had. Before boot and in tests there are none.
 */
export interface PetLinkPet { readonly id: string; readonly name: string; readonly level: number }

export interface PetLinkApi {
  /** The pets had, in roster order. */
  owned(): readonly PetLinkPet[];
  /** Pets v3: every pet in the roster, in order (level 0: not caught yet). */
  roster?(): readonly PetLinkPet[];
  /** The pet that comes into battles, or null (none had, or the Commander goes without). */
  equipped(): string | null;
  /**
   * A battle's wild pet, settled now (app Pets.settle: the treat used when it appears, the catch paid once; saved):
   * the catch's grant once it is caught, else null. The level calls it on WildPetAppeared and WildPetCaught.
   */
  settle?(sim: LevelSim): CatchGrant | null;
}

const NONE: PetLinkApi = { owned: () => [], equipped: () => null };
let api: PetLinkApi = NONE;
const listeners: (() => void)[] = [];

export const petLink = {
  set(a: PetLinkApi | null): void { api = a ?? NONE; petLink.changed(); },
  owned(): readonly PetLinkPet[] { return api.owned(); },
  /** Every pet in the roster (level 0 for one not caught yet); the pets had before boot or in tests. */
  roster(): readonly PetLinkPet[] { return api.roster?.() ?? api.owned(); },
  equipped(): string | null { return api.equipped(); },
  /** A pet's level now, 0 for a pet not had. */
  level(petId: string): number { return api.owned().find((p) => p.id === petId)?.level ?? 0; },
  has(petId: string): boolean { return api.owned().some((p) => p.id === petId); },
  name(petId: string): string { return api.owned().find((p) => p.id === petId)?.name ?? "pet"; },
  /** Settles a battle's wild pet now (null before boot, in tests, or with nothing to pay). */
  settle(sim: LevelSim): CatchGrant | null { return api.settle?.(sim) ?? null; },
  /** Something to re-read changed (a pet equipped or caught, a skin won): the cosmetics wear it again. */
  onChange(fn: () => void): () => void { listeners.push(fn); return () => { const i = listeners.indexOf(fn); if (i >= 0) listeners.splice(i, 1); }; },
  changed(): void { for (const fn of [...listeners]) fn(); },
};
